import { Close } from "@mui/icons-material";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import Link from "@mui/material/Link";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import clsx from "clsx";
import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import CreateOrganizationImage from "../../../assets/images/svgs/create-organization.svg";
import BriaInput from "../../../components/common/BriaInput/BriaInput.tsx";
import LinkConstants from "../../../constants/LinkConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { useAppStore } from "../../../hooks/useStores.tsx";
import BriaButton from "../BriaButton/BriaButton.tsx";
import styles from "./CreateOrganizationPopup.module.scss";

interface IProps {
	open: boolean;
	onClose: () => void;
	keepAlive?: boolean;
}

export default function CreateOrganizationPopup({ open, onClose, keepAlive }: IProps) {
	const initialValues = {
		organization: "",
	};
	const urlRegex = /(?:https?:\/\/|www\.)[^\s/$.?#].[^\s]*/i;
	const { authStore } = useAppStore();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState();

	const handleSubmit = async (values: any) => {
		let response = null;
		setLoading(true);
		try {
			setErrorMessage(undefined);
			response = await authStore.createOrganization(values.organization);
			await authStore.loadUserData();
			if (response.type === "success" && authStore.userOrganizations.length > 0) {
				try {
					const parentOrigin = new URL(import.meta.env.VITE_NEW_PLATFORM_URL as string).origin;
					window.parent.postMessage({ type: "MIGRATION_IFRAME_ORG_CREATED" }, parentOrigin);
				} catch {
					// If not running inside the migration iframe, postMessage is a no-op.
				}
				window.location.href = localStorage.getItem("destination_route") || RouterConstants.CONSOLE.fullPath;
			} else if (response.type === "failed") {
				setErrorMessage(response.message ?? t("pleaseTryAgainLater"));
			}
		} catch (e) {
			if (e instanceof AxiosError) {
				const respData = (e as AxiosError).response?.data as any;
				if (respData?.message === "email_not_verified") {
					// Creating an organization needs a verified email address.
					setErrorMessage(
						t("verifyEmailToCreateOrganization", {
							defaultValue: "Please verify your email address before creating an organization.",
						}) as any,
					);
				} else if (respData?.type === "failed" && respData?.message) {
					setErrorMessage(respData?.message);
				} else {
					Sentry.captureException(e);
					setErrorMessage(t("pleaseTryAgainLater") as any);
				}
			} else {
				Sentry.captureException(e);
				setErrorMessage(t("pleaseTryAgainLater") as any);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleOnClose = () => {
		if (keepAlive) return;
		onClose();
	};

	return (
		<Dialog
			classes={{
				paper: styles.paper,
			}}
			onClose={handleOnClose}
			open={open}
			maxWidth={false}
		>
			<Box className={styles.container}>
				{!keepAlive && (
					<IconButton onClick={handleOnClose} className={styles.closeButton}>
						<Close />
					</IconButton>
				)}
				<Box className={styles.content}>
					<Typography className={styles.title}>{t("setUpYourOrganization")}</Typography>
					<Box className={styles.descriptionContainer}>
						<Typography className={clsx(styles.description, styles.firstDescription)}>
							{t("setUpYourOrganizationDescription1")}
						</Typography>
						<Typography className={clsx(styles.description)}>
							{t("setUpYourOrganizationDescription2")}
						</Typography>
						<Typography className={styles.description}>
							{t("setUpYourOrganizationDescription3")}
							<Link
								onClick={(_e: React.MouseEvent<any>) => {
									window.open(LinkConstants.CONTACT_US_FORM_LINK, "_blank");
								}}
								className={styles.link}
							>
								{t("contact us.")}
							</Link>
						</Typography>
					</Box>
					<Formik
						initialValues={initialValues}
						validateOnMount={false}
						validateOnChange={false}
						validateOnBlur={false}
						validationSchema={Yup.object().shape({
							organization: Yup.string()
								.required(t("organizationIsRequired"))
								.test(
									"no-url",
									t("organizationNameMustNotContainUrl"),
									(value) => !value || !urlRegex.test(value),
								),
						})}
						onSubmit={handleSubmit}
					>
						{({ errors, touched, handleChange, handleBlur }) => (
							<Form noValidate>
								<Box className={styles.formContainer}>
									<BriaInput
										id="organization"
										label={t("setupYourOrganizationPopup.organizationName")}
										size="small"
										onChange={handleChange}
										error={(Boolean(errors.organization) && touched.organization) as boolean}
										onBlur={handleBlur}
										helperText={touched.organization ? errors.organization : ""}
										className={styles.textField}
									/>
									<BriaButton
										buttonType="primary"
										type="submit"
										color="secondary"
										className={styles.submitButton}
										loading={loading}
										disabled={loading}
									>
										{t("createOrganization")}
									</BriaButton>
									{errorMessage && (
										<Typography className={styles.error} color="secondary">
											<ErrorIcon className={styles.errorIcon} />
											{errorMessage}
										</Typography>
									)}
								</Box>
							</Form>
						)}
					</Formik>
				</Box>
				<Box className={styles.imageContainer}>
					<img alt="Create Organization image" src={CreateOrganizationImage} />
				</Box>
			</Box>
		</Dialog>
	);
}
